import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { getSharedConversation } from '../../api/chatApi';
import EnhancedMarkdown from '../../components/EnhancedMarkdown';
import { answerTextForShare } from '../../utils/chatRichCatalog';
import { stripLeakedCitationMarkers } from '../../utils/chatArtifactTags';
import WhatQuizElephant from '../../components/brand/WhatQuizElephant';
import './SharedChatPage.css';

/**
 * 공유된 대화 페이지
 * 누구나 접근 가능한 읽기 전용 페이지
 */
const SharedChatPage = () => {
  const { shareId } = useParams();
  
  const [sharedData, setSharedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 공유 대화 로드
  useEffect(() => {
    let cancelled = false;

    const fetchSharedConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getSharedConversation(shareId);
        if (!cancelled) setSharedData(response);
      } catch (err) {
        console.error('공유 대화 로드 실패:', err);
        if (!cancelled) {
          setError(err.message || '공유된 대화를 불러오는 데 실패했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (shareId) {
      fetchSharedConversation();
    }

    return () => {
      cancelled = true;
    };
  }, [shareId]);

  // 시간 포맷팅
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const getCategoryName = (category) => {
    const categoryNames = {
      pro_ai: 'PRO AI',
      record: '생기부 전문가',
      document: 'AI 한글',
      lesson_design: '수업 설계',
      parent_communication: '학부모 소통',
      student_counseling: '학생 상담',
      teacher_care: '교사 멘탈 케어',
      evaluation: '평가 관리',
      general: '자유로운 AI',
    };
    return categoryNames[category] || 'AI 대화';
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="shared-chat-page">
        <header className="shared-chat-topbar">
          <div className="shared-chat-brand" aria-label="왓퀴즈">
            <span className="shared-chat-brand-mark">
              <WhatQuizElephant size={32} />
            </span>
            <strong>왓퀴즈</strong>
          </div>
          <span className="shared-chat-readonly-label">공유받은 AI 대화</span>
        </header>
        <main className="shared-chat-main" aria-label="대화를 불러오는 중">
          <div className="shared-chat-summary-skeleton">
            <span />
            <span />
            <span />
          </div>
          <div className="shared-chat-message-skeleton is-user" />
          <div className="shared-chat-message-skeleton is-assistant" />
          <div className="shared-chat-message-skeleton is-user" />
        </main>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="shared-chat-page">
        <header className="shared-chat-topbar">
          <Link className="shared-chat-brand" to="/" aria-label="왓퀴즈 홈">
            <span className="shared-chat-brand-mark">
              <WhatQuizElephant size={32} />
            </span>
            <strong>왓퀴즈</strong>
          </Link>
          <span className="shared-chat-readonly-label">공유받은 AI 대화</span>
        </header>
        <main className="shared-chat-error">
          <div className="shared-chat-error-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M12 17h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>
          <h2>대화를 찾을 수 없습니다</h2>
          <p>{error}</p>
          <Link className="shared-chat-cta-button" to="/">
            왓퀴즈 둘러보기
          </Link>
        </main>
      </div>
    );
  }

  const categoryName = sharedData?.category_name
    || getCategoryName(sharedData?.category);
  const messages = sharedData?.messages || [];

  return (
    <div className="shared-chat-page">
      <Helmet>
        <title>{sharedData?.title || '공유받은 AI 대화'} | 왓퀴즈</title>
        <meta
          name="description"
          content="동료 선생님이 왓퀴즈에서 나눈 AI 대화를 확인해보세요."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="shared-chat-topbar">
        <Link className="shared-chat-brand" to="/" aria-label="왓퀴즈 홈">
          <span className="shared-chat-brand-mark">
            <WhatQuizElephant size={32} />
          </span>
          <strong>왓퀴즈</strong>
        </Link>
        <span className="shared-chat-readonly-label">공유받은 AI 대화</span>
      </header>

      <main className="shared-chat-main">
        <section className="shared-chat-info" aria-labelledby="shared-chat-title">
          <span className="shared-chat-eyebrow">공유받은 대화</span>
          <h1 className="shared-chat-title" id="shared-chat-title">
            {sharedData?.title || '왓퀴즈 AI와 나눈 대화'}
          </h1>
          <div className="shared-chat-meta">
            <span>{categoryName}</span>
            <span aria-hidden="true">·</span>
            <span>메시지 {messages.length}개</span>
            {sharedData?.created_at && (
              <>
                <span aria-hidden="true">·</span>
                <time dateTime={sharedData.created_at}>
                  {formatTime(sharedData.created_at)}
                </time>
              </>
            )}
          </div>
          <p className="shared-chat-readonly-note">이 대화는 읽기 전용이에요.</p>
        </section>

        <section className="shared-chat-messages" aria-label="공유된 메시지">
          {messages.map((message, index) => (
            <article
              key={`${message.role}-${message.timestamp || index}`}
              className={`shared-message ${message.role === 'user' ? 'user' : 'assistant'}`}
            >
              <div className="shared-message-content">
                <span className="shared-message-role">
                  {message.role === 'user' ? '선생님' : '왓퀴즈 AI'}
                </span>
                <div className="shared-message-text">
                  {message.role === 'assistant' ? (
                    <EnhancedMarkdown 
                      content={answerTextForShare(stripLeakedCitationMarkers(message.content), [])}
                      contentCategory={message.category}
                      enableMath={true}
                    />
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                  )}
                </div>
                {message.timestamp && (
                  <time className="shared-message-time" dateTime={message.timestamp}>
                    {formatTime(message.timestamp)}
                  </time>
                )}
              </div>
            </article>
          ))}
        </section>

        <section className="shared-chat-next">
          <div className="shared-chat-next-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>
          <div className="shared-chat-next-copy">
            <h2>나도 AI에게 물어보세요</h2>
            <p>생기부, 수업 자료, 평가 업무를 대화로 빠르게 해결해요.</p>
          </div>
          <div className="shared-chat-next-actions">
            <Link to="/chat" className="shared-chat-primary-cta">
              AI 대화 시작하기
            </Link>
            <Link to="/" className="shared-chat-secondary-cta">
              왓퀴즈 둘러보기
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SharedChatPage;

